/**
 * WHAT: Database topology display section for audit/attestation reports.
 * WHY: Auditors need to understand database deployment topology (RAC, Always On,
 *      Group Replication, Multi-DC) because access rights replicate across nodes --
 *      attesting one database effectively grants access to all replicas.
 */

import React from 'react';

/**
 * WHAT: Table rendering database topology details including deployment type,
 *       role, version, and node/instance breakdown per technology.
 * WHY: Provides audit-ready topology visibility so attesters understand the
 *      blast radius of each access certification decision.
 */
const TopologySection = ({ topology }) => {
  if (!topology || topology.length === 0) {
    return null;
  }

  /* WHAT: Returns a CSS badge class based on whether the deployment is high-availability.
     WHY: HA deployments (RAC, Always On, etc.) get a warning badge to highlight the broader access scope. */
  const getDeploymentBadgeClass = (deploymentType) => {
    const highAvailability = ['RAC', 'ALWAYS_ON', 'GROUP_REPLICATION', 'MULTI_DC'];
    return highAvailability.includes(deploymentType) ? 'bg-warning text-dark' : 'bg-secondary';
  };

  /* WHAT: Formats Oracle RAC instances and standby databases as an inline summary.
     WHY: Oracle HA uses RAC (multiple instances) and Data Guard (standby), both relevant for access scope. */
  const getOracleNodesSummary = (topo) => {
    const parts = [];
    if (topo.instances && topo.instances.length > 0) {
      parts.push(
        <div key="rac" className="mb-1">
          <strong>RAC:</strong>{' '}
          {topo.instances.map((inst, idx) => (
            <span key={idx}>
              <code>{inst.name}</code>@{inst.host}
              {idx < topo.instances.length - 1 ? ', ' : ''}
            </span>
          ))}
        </div>
      );
    }
    if (topo.standby_databases && topo.standby_databases.length > 0) {
      parts.push(
        <div key="standby">
          <strong>Standby:</strong>{' '}
          {topo.standby_databases.map((sb, idx) => (
            <span key={idx}>
              <code>{sb.name}</code>{sb.host ? `@${sb.host}` : ''}
              {idx < topo.standby_databases.length - 1 ? ', ' : ''}
            </span>
          ))}
        </div>
      );
    }
    return parts.length > 0 ? parts : <span className="text-muted">Single Instance</span>;
  };

  /* WHAT: Formats MSSQL Always On Availability Group replicas with their roles.
     WHY: MSSQL HA uses AG replicas with PRIMARY/SECONDARY roles that share the same access rights. */
  const getMSSQLNodesSummary = (topo) => {
    if (topo.replicas && topo.replicas.length > 0) {
      return (
        <div>
          {topo.replicas.map((r, idx) => (
            <div key={idx}>
              <code>{r.server_name}</code>{' '}
              <span className={`badge badge-sm ${r.role === 'PRIMARY' ? 'bg-success' : 'bg-info'}`}>{r.role}</span>
            </div>
          ))}
        </div>
      );
    }
    return <span className="text-muted">Single Instance</span>;
  };

  /* WHAT: Formats MySQL Group Replication members with host:port and role.
     WHY: MySQL GR members replicate user grants, so all members share the same access configuration. */
  const getMySQLNodesSummary = (topo) => {
    if (topo.members && topo.members.length > 0) {
      return (
        <div>
          {topo.members.map((m, idx) => (
            <div key={idx}>
              <code>{m.host}:{m.port}</code>{' '}
              <span className={`badge badge-sm ${m.role === 'PRIMARY' ? 'bg-success' : 'bg-info'}`}>{m.role}</span>
            </div>
          ))}
        </div>
      );
    }
    return <span className="text-muted">Single Instance</span>;
  };

  /* WHAT: Formats Cassandra datacenter names with node counts.
     WHY: Cassandra's multi-DC topology means roles/permissions replicate across all datacenters. */
  const getCassandraNodesSummary = (topo) => {
    if (topo.datacenters && topo.datacenters.length > 0) {
      return (
        <div>
          {topo.datacenters.map((dc, idx) => (
            <div key={idx}>
              <strong>{dc.name}:</strong> {dc.nodes?.length || 0} nodes
            </div>
          ))}
        </div>
      );
    }
    return <span>{topo.total_nodes || 1} node(s)</span>;
  };

  /* WHAT: Dispatches to the technology-specific node summary renderer based on the database type.
     WHY: Each database technology has a different HA model (RAC, AG, GR, Multi-DC) requiring distinct formatting. */
  const getNodesSummary = (topo) => {
    const tech = topo.technology?.toUpperCase();
    switch (tech) {
      case 'ORACLE': return getOracleNodesSummary(topo);
      case 'SQL SERVER':
      case 'MSSQL': return getMSSQLNodesSummary(topo);
      case 'MYSQL': return getMySQLNodesSummary(topo);
      case 'CASSANDRA': return getCassandraNodesSummary(topo);
      default: return <span className="text-muted">N/A</span>;
    }
  };

  return (
    <div className="card shadow-sm mb-4">
      <div className="card-header bg-secondary text-white">
        <h5 className="mb-0">
          <i className="fas fa-network-wired me-2"></i>
          Database Topology
          <small className="ms-2 fw-normal">({topology.length} database{topology.length > 1 ? 's' : ''} in scope)</small>
        </h5>
      </div>
      <div className="card-body p-0">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead className="table-dark">
              <tr>
                <th style={{ width: '100px' }}>DB Identifier</th>
                <th style={{ width: '120px' }}>Database</th>
                <th style={{ width: '80px' }}>Version</th>
                <th style={{ width: '90px' }}>Deployment</th>
                <th style={{ width: '70px' }}>Role</th>
                <th>Nodes / Instances</th>
              </tr>
            </thead>
            <tbody>
              {topology.map((topo, index) => (
                <tr key={index}>
                  <td>
                    <code>{topo.database_identifier || 'N/A'}</code>
                  </td>
                  <td>
                    <strong>{topo.database_name}</strong>
                  </td>
                  <td><small>{topo.version || 'N/A'}</small></td>
                  <td>
                    <span className={`badge ${getDeploymentBadgeClass(topo.deployment_type)}`}>
                      {topo.deployment_type || 'STANDALONE'}
                    </span>
                  </td>
                  <td>
                    {topo.database_role && (
                      <span className={`badge ${topo.database_role === 'PRIMARY' ? 'bg-success' : 'bg-secondary'}`}>
                        {topo.database_role}
                      </span>
                    )}
                  </td>
                  <td style={{ fontSize: '0.85rem' }}>
                    {getNodesSummary(topo)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer with general audit note */}
        {topology.some(t => t.deployment_type !== 'STANDALONE') && (
          <div className="alert alert-info mb-0 rounded-0 border-0 py-2">
            <i className="fas fa-info-circle me-2"></i>
            <strong>Note:</strong> High-availability deployments (RAC, Always On AG, Group Replication, Multi-DC) replicate access rights across all nodes.
            Attesting access for one database grants the user access to all replicas/instances in the topology.
          </div>
        )}
      </div>
    </div>
  );
};

export default TopologySection;
